import { FileQuestion, Inbox, Lock, RefreshCw, ServerCrash } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import { tAny } from '@/i18n'
import { cn } from '@/utils/cn'

interface StateProps {
  title: string
  description?: ReactNode
  icon?: ReactNode
  action?: ReactNode
  className?: string
  compact?: boolean
}

function StateShell({
  title,
  description,
  icon,
  action,
  className,
  compact,
  tone = 'brand',
}: StateProps & { tone?: 'brand' | 'danger' | 'neutral' }) {
  const toneClass = {
    brand: 'bg-brand-50 text-brand-600 ring-brand-100',
    danger: 'bg-danger-50 text-danger-600 ring-danger-100',
    neutral: 'bg-surface-sunken text-muted ring-line',
  }[tone]
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        compact ? 'px-4 py-8' : 'px-6 py-16',
        className,
      )}
    >
      {icon ? (
        <span
          className={cn('mb-4 grid size-12 place-items-center rounded-2xl ring-8 [&_svg]:size-6', toneClass)}
        >
          {icon}
        </span>
      ) : null}
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      {description ? <p className="mt-1.5 max-w-sm text-sm text-muted">{description}</p> : null}
      {action ? <div className="mt-5 flex flex-wrap justify-center gap-2">{action}</div> : null}
    </div>
  )
}

export function EmptyState(props: StateProps) {
  return <StateShell icon={<Inbox />} tone="neutral" {...props} />
}

export function ErrorState({ onRetry, ...props }: Partial<StateProps> & { onRetry?: () => void }) {
  const { t } = useTranslation()
  return (
    <StateShell
      icon={<ServerCrash />}
      tone="danger"
      title={props.title ?? t('Something went wrong')}
      description={props.description ?? t('We could not load this data. Please try again.')}
      action={
        onRetry ? (
          <Button variant="secondary" leftIcon={<RefreshCw />} onClick={onRetry}>
            {t('Try again')}
          </Button>
        ) : (
          props.action
        )
      }
      className={props.className}
      compact={props.compact}
    />
  )
}

export function ForbiddenState({ homeHref = '/' }: { homeHref?: string }) {
  const { t } = useTranslation()
  return (
    <StateShell
      icon={<Lock />}
      tone="danger"
      title={t("You don't have access to this page")}
      description={t(
        "Your role doesn't include permission for this area. Ask an administrator if you think this is a mistake.",
      )}
      action={
        <Button asChild variant="secondary">
          <Link to={homeHref}>{t('Go to my dashboard')}</Link>
        </Button>
      }
    />
  )
}

export function NotFoundState({
  homeHref = '/',
  resource = 'page',
}: {
  homeHref?: string
  resource?: string
}) {
  const { t } = useTranslation()
  return (
    <StateShell
      icon={<FileQuestion />}
      title={tAny(`This ${resource} could not be found`)}
      description={t('It may have been moved or deleted, or the link is incorrect.')}
      action={
        <Button asChild variant="secondary">
          <Link to={homeHref}>{t('Back to safety')}</Link>
        </Button>
      }
    />
  )
}

export function LoadingState({ label, className }: { label?: string; className?: string }) {
  const { t } = useTranslation()
  return (
    <div className={cn('flex min-h-48 items-center justify-center text-brand-600', className)}>
      <Spinner label={label ?? t('Loading…')} className="size-6" />
    </div>
  )
}

export function FullPageLoader() {
  const { t } = useTranslation()
  return (
    <div className="grid min-h-dvh place-items-center bg-canvas">
      <div className="flex flex-col items-center gap-4">
        <span className="bg-brand-gradient grid size-12 place-items-center rounded-2xl text-white shadow-brand">
          <Spinner className="size-6" />
        </span>
        <p className="text-sm text-muted" role="status">
          {t('Loading your workspace…')}
        </p>
      </div>
    </div>
  )
}
